// src/Muestras/Drawer/sections/DatesSection.jsx
import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Box,
  Tooltip,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  drawerAccordionSx,
  drawerAccordionSummarySx,
  outlinedFieldSx,
  sectionChipSx,
  muestraDatePickerPortalProps,
} from "../drawerSurfaceSx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { es } from "date-fns/locale";

import { useTranslation } from "react-i18next";
import {
  formatMuestraDateForAPI,
  parseMuestraDate,
} from "../../../../utils/dateUtils";

export default function DatesSection({
  muestra,
  setMuestra,
  canEditGetSupplier,
}) {
  const theme = useTheme();
  const accSx = drawerAccordionSx(theme);
  const sumSx = drawerAccordionSummarySx(theme);
  const fin = outlinedFieldSx(theme);
  const chipSx = sectionChipSx(theme);
  const { t } = useTranslation();

  const dateTooltipProps = {
    arrow: true,
    placement: "top",
    // Por debajo del calendario (z-index 1600)
    slotProps: {
      popper: { sx: { zIndex: 1400 } },
    },
  };

  const handleDate = (field, date) => {
    if (!date) {
      setMuestra((prev) => ({ ...prev, [field]: null }));
      return;
    }

    setMuestra((prev) => ({
      ...prev,
      [field]: formatMuestraDateForAPI(date),
    }));
  };

  const safeDate = (value) => parseMuestraDate(value);

  const filledDates = [
    muestra.FECHA_PREVISTA,
    muestra.FECHA_PREVISTA_FABRICA,
    muestra.FECHA_PETICION,
    muestra.FECHA_GET_SUPPLIER,
    muestra.FECHA_ENVIO,
    muestra.FECHA_RECEPCION,
  ].filter(Boolean).length;

  return (
    <Accordion defaultExpanded={false} sx={accSx}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={sumSx}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", mr: 0.5 }}>
          {t("Samples.dates")}
        </Typography>
        <Chip
          size="small"
          label={`${filledDates}/6`}
          sx={chipSx}
          onClick={(e) => e.stopPropagation()}
        />
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 2, pb: 2 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            },
            gap: 2,
            width: "100%",
          }}
        >
          <Tooltip
            title={t("Samples.tooltipFechaPrevista")}
            {...dateTooltipProps}
          >
            <Box>
              <DatePicker
                selected={safeDate(muestra.FECHA_PREVISTA)}
                onChange={(date) => handleDate("FECHA_PREVISTA", date)}
                dateFormat="dd/MM/yyyy"
                locale={es}
                {...muestraDatePickerPortalProps}
                customInput={
                  <TextField
                    size="small"
                    label={t("Samples.FECHA_PREVISTA")}
                    fullWidth
                    sx={fin}
                  />
                }
              />
            </Box>
          </Tooltip>
          <Tooltip
            title={t("Samples.tooltipFechaPrevistaFabrica")}
            {...dateTooltipProps}
          >
            <Box className="fecha-prevista-fabrica-editable">
              <DatePicker
                selected={safeDate(muestra.FECHA_PREVISTA_FABRICA)}
                onChange={(date) => handleDate("FECHA_PREVISTA_FABRICA", date)}
                dateFormat="dd/MM/yyyy"
                locale={es}
                {...muestraDatePickerPortalProps}
                customInput={
                  <TextField
                    size="small"
                    label={t("Samples.FECHA_PREVISTA_FABRICA")}
                    fullWidth
                    sx={fin}
                  />
                }
              />
            </Box>
          </Tooltip>
          <Tooltip
            title={t("Samples.tooltipFechaPeticion")}
            {...dateTooltipProps}
          >
            <Box>
              <DatePicker
                selected={safeDate(muestra.FECHA_PETICION)}
                onChange={(date) => handleDate("FECHA_PETICION", date)}
                dateFormat="dd/MM/yyyy"
                locale={es}
                disabled
                {...muestraDatePickerPortalProps}
                customInput={
                  <TextField
                    size="small"
                    label={t("Samples.FECHA_PETICION")}
                    fullWidth
                    disabled
                    sx={fin}
                  />
                }
              />
            </Box>
          </Tooltip>
          <Tooltip
            title={t("Samples.tooltipFechaGetSupplier")}
            {...dateTooltipProps}
          >
            <Box>
              <DatePicker
                selected={safeDate(muestra.FECHA_GET_SUPPLIER)}
                onChange={(date) => {
                  if (!canEditGetSupplier) return;
                  handleDate("FECHA_GET_SUPPLIER", date);
                }}
                dateFormat="dd/MM/yyyy"
                locale={es}
                disabled
                {...muestraDatePickerPortalProps}
                customInput={
                  <TextField
                    size="small"
                    label={t("Samples.FECHA_GET_SUPPLIER")}
                    fullWidth
                    disabled
                    sx={fin}
                  />
                }
              />
            </Box>
          </Tooltip>
          <Tooltip
            title={t("Samples.tooltipFechaEnvio")}
            {...dateTooltipProps}
          >
            <Box>
              <DatePicker
                selected={safeDate(muestra.FECHA_ENVIO)}
                dateFormat="dd/MM/yyyy"
                locale={es}
                disabled
                {...muestraDatePickerPortalProps}
                customInput={
                  <TextField
                    size="small"
                    label={t("Samples.FECHA_ENVIO")}
                    fullWidth
                    disabled
                    sx={fin}
                  />
                }
              />
            </Box>
          </Tooltip>
          <Tooltip
            title={t("Samples.tooltipFechaRecepcion")}
            {...dateTooltipProps}
          >
            <Box>
              <DatePicker
                selected={safeDate(muestra.FECHA_RECEPCION)}
                onChange={(date) => handleDate("FECHA_RECEPCION", date)}
                dateFormat="dd/MM/yyyy"
                locale={es}
                disabled
                {...muestraDatePickerPortalProps}
                customInput={
                  <TextField
                    size="small"
                    label={t("Samples.FECHA_RECEPCION")}
                    fullWidth
                    disabled
                    sx={fin}
                  />
                }
              />
            </Box>
          </Tooltip>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
